import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { gql, useClient } from 'urql';

const DECRYPT_TALENT_DISCOVERY_DATA = gql`
  query DecryptTalentDiscoveryData($input: String!) {
    decryptString(input: $input)
  }
`;

type DecryptTalentDiscoveryDataResponse = {
  decryptString: string;
};

type DecryptTalentDiscoveryDataVariables = {
  input: string;
};

type DecryptedValuesByUser = Record<string, Record<string, string>>;
type DecryptingByUser = Record<string, Record<string, boolean>>;

type DecryptLookupParams = {
  userKey: string;
  fieldKey: string;
};

type DecryptRequestParams = DecryptLookupParams & {
  encryptedValue: string;
};

interface TrialUnblurContextValue {
  getDecryptedValue: (params: DecryptLookupParams) => string | undefined;
  isDecrypting: (params: DecryptLookupParams) => boolean;
  requestDecrypt: (params: DecryptRequestParams) => Promise<void>;
}

const TrialUnblurContext = createContext<TrialUnblurContextValue | null>(null);

const setDecryptingValue = (
  prev: DecryptingByUser,
  userKey: string,
  fieldKey: string,
  nextValue: boolean
): DecryptingByUser => ({
  ...prev,
  [userKey]: {
    ...(prev[userKey] ?? {}),
    [fieldKey]: nextValue,
  },
});

const setDecryptedValue = (
  prev: DecryptedValuesByUser,
  userKey: string,
  fieldKey: string,
  nextValue: string
): DecryptedValuesByUser => ({
  ...prev,
  [userKey]: {
    ...(prev[userKey] ?? {}),
    [fieldKey]: nextValue,
  },
});

export const TrialUnblurProvider = ({ children }: { children: ReactNode }) => {
  const client = useClient();
  const [decryptedValues, setDecryptedValues] = useState<DecryptedValuesByUser>(
    {}
  );
  const [decryptingValues, setDecryptingValues] = useState<DecryptingByUser>(
    {}
  );

  const getDecryptedValue = useCallback(
    ({ userKey, fieldKey }: DecryptLookupParams) =>
      decryptedValues[userKey]?.[fieldKey],
    [decryptedValues]
  );

  const isDecrypting = useCallback(
    ({ userKey, fieldKey }: DecryptLookupParams) =>
      Boolean(decryptingValues[userKey]?.[fieldKey]),
    [decryptingValues]
  );

  const requestDecrypt = useCallback(
    async ({ userKey, fieldKey, encryptedValue }: DecryptRequestParams) => {
      const normalizedEncryptedValue = encryptedValue.trim();
      const alreadyDecrypted = Boolean(decryptedValues[userKey]?.[fieldKey]);
      const inFlight = Boolean(decryptingValues[userKey]?.[fieldKey]);

      if (
        !userKey ||
        !fieldKey ||
        normalizedEncryptedValue.length === 0 ||
        alreadyDecrypted ||
        inFlight
      ) {
        return;
      }

      setDecryptingValues((prev) =>
        setDecryptingValue(prev, userKey, fieldKey, true)
      );

      try {
        const result = await client
          .query<
            DecryptTalentDiscoveryDataResponse,
            DecryptTalentDiscoveryDataVariables
          >(DECRYPT_TALENT_DISCOVERY_DATA, {
            input: encryptedValue,
          })
          .toPromise();

        const decryptedValue = result.data?.decryptString ?? '';
        const hasDecryptedValue = decryptedValue.trim().length > 0;

        if (hasDecryptedValue) {
          setDecryptedValues((prev) =>
            setDecryptedValue(prev, userKey, fieldKey, decryptedValue)
          );
        }
      } finally {
        setDecryptingValues((prev) =>
          setDecryptingValue(prev, userKey, fieldKey, false)
        );
      }
    },
    [client, decryptedValues, decryptingValues]
  );

  const contextValue = useMemo(
    () => ({
      getDecryptedValue,
      isDecrypting,
      requestDecrypt,
    }),
    [getDecryptedValue, isDecrypting, requestDecrypt]
  );

  return (
    <TrialUnblurContext.Provider value={contextValue}>
      {children}
    </TrialUnblurContext.Provider>
  );
};

export const useTrialUnblur = (): TrialUnblurContextValue | null =>
  useContext(TrialUnblurContext);
